import React, { useState, useEffect, useMemo } from 'react';
import { usePortfolioStream } from '../../hooks/usePortfolioStream';
import { WhatIfModal } from '../../components/toplist/human/modals/WhatIfModal';

interface NameMapItem {
  deposit_register_id: number;
  user_name: string;
  // --- A Node service (getActiveDepositsWithNamesService) ezt adja vissza:
  // "HUMAN" (Systems.HPM) vagy "BRAVE" (Systems.ALT). ---
  strategyId?: string;
}

// --- A fülekhez használt stratégia-típus ---
type StrategyTab = 'HUMAN' | 'BRAVE';

// --- Egy helyen döntjük el, hogy egy név-rekord melyik stratégiához tartozik.
// A service strategyId-t ad ("HUMAN" | "BRAVE"); ismeretlen érték esetén HUMAN. ---
const resolveStrategy = (item: NameMapItem): StrategyTab => {
  return item.strategyId && item.strategyId.toUpperCase() === 'BRAVE' ? 'BRAVE' : 'HUMAN';
};

export const TopListTable = () => {
  const [nameMap, setNameMap] = useState<Record<number, string>>({});
  // --- ÚJ: id -> stratégia leképezés, a fül-szűréshez ---
  const [strategyMap, setStrategyMap] = useState<Record<number, StrategyTab>>({});
  const [searchTerm, setSearchTerm] = useState<string>('');

  // --- ÚJ: az aktív fül ---
  const [activeTab, setActiveTab] = useState<StrategyTab>('HUMAN');

  // --- WHAT-IF KALKULÁTOR ÁLLAPOTOK ---
  const [btcPriceInput, setBtcPriceInput] = useState<string>('');
  const [ethPriceInput, setEthPriceInput] = useState<string>('');
  const [hasAutofilled, setHasAutofilled] = useState<boolean>(false);

  const [whatIfModalId, setWhatIfModalId] = useState<number | null>(null);

  // 1. Felhasználónevek betöltése
  useEffect(() => {
    fetch(`${process.env.REACT_APP_SAPI_URL}/active-deposits-names`)
      .then((res) => res.json())
      .then((response) => {
        const dictionary: Record<number, string> = {};
        const strategyDict: Record<number, StrategyTab> = {};
        response?.data?.forEach((item: NameMapItem) => {
          if (item && item.deposit_register_id) {
            dictionary[item.deposit_register_id] = item.user_name || 'Ismeretlen felhasználó';
            // --- ÚJ: stratégia eltárolása id szerint ---
            strategyDict[item.deposit_register_id] = resolveStrategy(item);
          }
        });
        setNameMap(dictionary);
        setStrategyMap(strategyDict);
      })
      .catch((err) => console.error('Hiba a nevek betöltésekor:', err));
  }, []);

  // --- MÓDOSÍTVA: csak az aktív fülhöz tartozó ID-ket streameljük ---
  const dynamicIds = useMemo(() => {
    return Object.keys(nameMap)
      .map((id) => parseInt(id, 10))
      .filter((id) => strategyMap[id] === activeTab);
  }, [nameMap, strategyMap, activeTab]);

  // --- ÚJ: mennyi deposit tartozik az egyes fülekhez (a fül-feliratokhoz) ---
  const tabCounts = useMemo(() => {
    const counts = { HUMAN: 0, BRAVE: 0 };
    Object.values(strategyMap).forEach((s) => {
      counts[s] += 1;
    });
    return counts;
  }, [strategyMap]);

  // 2. Élő gRPC stream hívása (mostantól a fül-váltás új ID-listát ad, ami újraindítja a streamet)
  const { topList, loading } = usePortfolioStream(dynamicIds);

  // AUTOFILL LOGIKA: Amikor bejön az első élő Bybit ár a Go-ból, kitöltjük a mezőket
  useEffect(() => {
    if (!hasAutofilled && topList && topList.length > 0) {
      const firstValidItem = topList.find((item) => item && (item.btc_live_price || 0) > 0);
      if (firstValidItem) {
        setBtcPriceInput(Math.round(firstValidItem.btc_live_price || 0).toString());
        setEthPriceInput(Math.round(firstValidItem.eth_live_price || 0).toString());
        setHasAutofilled(true);
      }
    }
  }, [topList, hasAutofilled]);

  const computedTopList = useMemo(() => {
    const targetBtcPrice = parseFloat(btcPriceInput) || 0;
    const targetEthPrice = parseFloat(ethPriceInput) || 0;

    if (!topList || !Array.isArray(topList)) return [];

    return topList.map((item) => {
      if (!item) return item;

      const liveBtcPrice = item.btc_live_price || 0;
      const liveEthPrice = item.eth_live_price || 0;
      const btcQty = item.btc_quantity || 0;
      const ethQty = item.eth_quantity || 0;

      const btcDeltaUSD = btcQty * (targetBtcPrice - liveBtcPrice);
      const ethDeltaUSD = ethQty * (targetEthPrice - liveEthPrice);
      const totalDeltaUSD = btcDeltaUSD + ethDeltaUSD;

      const totalDeltaEUR = totalDeltaUSD / 1.1;

      const fiktivPortfolioUSD = (item.current_value_usd || 0) + totalDeltaUSD;
      const fiktivPortfolioEUR = (item.current_value_eur || 0) + totalDeltaEUR;

      const currentLivePnl = item.pnl_percent || 0;

      const investedEur = item.invested_amount_eur || 0;
      const capitalRatioEur = item.capital_ratio_eur || 0;
      const bonusEur = item.bonus_eur || 0;

      // FIX BÁZIS: befizetés + tőkerész + bónusz.
      // Ez sosem mozdul, amikor a BTC/ETH teszt-árat módosítod —
      // csak a fiktivPortfolioEUR (számláló) változik.
      const komplexBazisEUR = investedEur + capitalRatioEur + bonusEur;

      let computedPnlPercent = currentLivePnl;
      if (komplexBazisEUR > 0) {
        computedPnlPercent = ((fiktivPortfolioEUR - komplexBazisEUR) / komplexBazisEUR) * 100;
      }

      // Fejlesztői konzisztencia-check: ha a teszt-árak = live árak,
      // a computedPnlPercent-nek egyeznie kell a backend currentLivePnl-jével.
      // Ha nem, a backend és a frontend bázis-definíciója eltér valahol.
      if (totalDeltaUSD === 0 && Math.abs(computedPnlPercent - currentLivePnl) > 0.5) {
        console.warn(
          `[PNL ELTÉRÉS] id=${item.deposit_register_id}: backend=${currentLivePnl.toFixed(
            2
          )}%, frontend=${computedPnlPercent.toFixed(2)}%`
        );
      }

      return {
        ...item,
        current_value_usd: fiktivPortfolioUSD,
        current_value_eur: fiktivPortfolioEUR,
        pnl_percent: computedPnlPercent,
      };
    });
  }, [topList, btcPriceInput, ethPriceInput]);

  // Szűrés és sorba rendezés
  const filteredAndSortedTopList = useMemo(() => {
    let list = [...computedTopList];

    // --- ÚJ: biztonsági szűrés a fülre is.
    // A stream elvileg csak az aktív fül ID-jeit adja, de fül-váltáskor
    // egy pillanatra a régi elemek még bent lehetnek — ez kiszűri őket. ---
    list = list.filter((item) => item && strategyMap[item.deposit_register_id] === activeTab);

    if (searchTerm && searchTerm.trim()) {
      const search = searchTerm.toLowerCase();
      list = list.filter((item) => {
        if (!item) return false;
        const name = (nameMap[item.deposit_register_id] || '').toLowerCase();
        const idStr = `#${item.deposit_register_id || ''}`;
        return name.includes(search) || idStr.includes(search);
      });
    }
    return list.sort((a, b) => (b?.pnl_percent || 0) - (a?.pnl_percent || 0));
  }, [computedTopList, nameMap, searchTerm, strategyMap, activeTab]);

  const handleResetPrices = () => {
    if (topList && topList.length > 0) {
      const firstValidItem = topList.find((item) => item && (item.btc_live_price || 0) > 0);
      if (firstValidItem) {
        setBtcPriceInput(Math.round(firstValidItem.btc_live_price || 0).toString());
        setEthPriceInput(Math.round(firstValidItem.eth_live_price || 0).toString());
      }
    }
  };

  const activeModalItem = useMemo(
    () =>
      filteredAndSortedTopList.find((it) => it && it.deposit_register_id === whatIfModalId) || null,
    [filteredAndSortedTopList, whatIfModalId]
  );

  // --- ÚJ: a fülek definíciója a rendereléshez ---
  const tabs: { key: StrategyTab; label: string }[] = [
    { key: 'HUMAN', label: 'HUMAN' },
    { key: 'BRAVE', label: 'BRAVE' },
  ];

  return (
    <div className="p-6 md:p-8 font-sans text-gray-700 bg-gray-50/50 min-h-screen antialiased">
      <div className="max-w-7xl mx-auto mb-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <div>
            <h1 className="text-xl font-semibold tracking-tight text-gray-900">
              HDC - Portfolio szimulációk
            </h1>
          </div>
          {loading && (
            <div className="flex items-center gap-2 self-start sm:self-center bg-blue-50 text-blue-600 px-3 py-1 rounded-full text-xs font-medium border border-blue-100 animate-pulse">
              <span className="w-1.5 h-1.5 rounded-full bg-blue-500"></span>
              Streamelés...
            </div>
          )}
        </div>

        {/* --- ÚJ: STRATÉGIA FÜLEK (HUMAN / BRAVE) --- */}
        <div className="mb-6 flex items-center gap-1 border-b border-gray-200">
          {tabs.map((tab) => {
            const isActive = activeTab === tab.key;
            return (
              <button
                key={tab.key}
                onClick={() => {
                  setActiveTab(tab.key);
                  setWhatIfModalId(null); // fül-váltáskor csukjuk a modalt, ha nyitva volt
                }}
                className={`relative px-4 py-2.5 text-sm font-medium transition-colors -mb-px border-b-2 ${
                  isActive
                    ? 'border-gray-900 text-gray-900'
                    : 'border-transparent text-gray-400 hover:text-gray-600'
                }`}
              >
                {tab.label}
                <span
                  className={`ml-2 inline-flex items-center justify-center px-1.5 py-0.5 rounded-full text-xxs font-semibold ${
                    isActive ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-500'
                  }`}
                >
                  {tabCounts[tab.key]}
                </span>
              </button>
            );
          })}
        </div>

        {/* VEZÉRLŐPULT */}
        <div className="mb-6 p-4 bg-white border border-gray-200 shadow-sm rounded-xl grid grid-cols-1 md:grid-cols-4 gap-4 items-end">
          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">
              Teszt BTC Árfolyam ($)
            </label>
            <input
              type="number"
              value={btcPriceInput}
              onChange={(e) => setBtcPriceInput(e.target.value)}
              className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg text-gray-900 font-mono focus:outline-none focus:border-gray-400"
              placeholder="BTC Ár USD"
            />
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">
              Teszt ETH Árfolyam ($)
            </label>
            <input
              type="number"
              value={ethPriceInput}
              onChange={(e) => setEthPriceInput(e.target.value)}
              className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg text-gray-900 font-mono focus:outline-none focus:border-gray-400"
              placeholder="ETH Ár USD"
            />
          </div>
          <div>
            <button
              onClick={handleResetPrices}
              className="w-full px-4 py-2 text-sm font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
            >
              Visszaállítás élő árakra
            </button>
          </div>
          <div className="relative">
            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">
              Gyorskeresés
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <svg
                  className="h-4 w-4 text-gray-400"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                  />
                </svg>
              </div>
              <input
                type="text"
                placeholder="Név vagy ID..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-9 pr-4 py-2 text-sm border border-gray-200 rounded-lg text-gray-900 focus:outline-none focus:border-gray-400"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Táblázat */}
      <div className="max-w-7xl mx-auto overflow-hidden bg-white border border-gray-200/80 rounded-xl shadow-sm">
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse text-xs md:text-sm">
            <thead>
              <tr className="bg-gray-50/70 border-b border-gray-200 text-gray-500 font-medium tracking-wide">
                <th className="p-3.5 w-12 text-center text-gray-400">#</th>
                <th className="p-3.5 w-16">ID</th>
                <th className="p-3.5 font-semibold text-gray-600">Név</th>
                <th className="p-3.5">Stratégia</th>
                {/*<th className="p-3.5">Eszközök & Megoszlás (USD)</th>*/}
                <th className="p-3.5 text-right">Befektetett</th>
                <th className="p-3.5 text-right">Bónusz / Tőkerész</th>
                <th className="p-3.5 text-right">Szimulált érték</th>
                <th className="p-3.5 text-right">PnL / Kivét</th>
                <th className="p-3.5 w-24 text-center">Mi lenne ha?</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filteredAndSortedTopList.map((item, index) => {
                if (!item) return null;
                const pnl = item.pnl_percent || 0;
                const isProfit = pnl >= 0;

                // Bónusz & Tőkerész logika
                const bonus = item.bonus_eur || 0;
                const capitalRatio = item.capital_ratio_eur || 0;
                const hasBonusOrCapital = bonus > 0 || capitalRatio > 0;

                // Új USD alapú alszámlák kinyerése
                const liqUsd = item.total_liquidity_usd || 0;
                const mineUsd = item.total_mining_usd || 0;
                const vaultUsd = item.total_altcoin_vault_usd || 0;

                // --- MÓDOSÍTVA: a stratégia-címke elsődlegesen a name-endpointból,
                // másodlagosan a stream strategy mezőjéből, végül HUMAN ---
                const strategyLabel = strategyMap[item.deposit_register_id] || item.strategy || 'HUMAN';

                return (
                  <tr
                    key={item.deposit_register_id || index}
                    className="hover:bg-gray-50/50 transition-colors"
                  >
                    <td className="p-3.5 text-center text-gray-400 font-medium tabular-nums">
                      {index + 1}
                    </td>
                    <td className="p-3.5 font-mono text-gray-400 text-xs">
                      #{item.deposit_register_id || 0}
                    </td>
                    <td className="p-3.5 font-medium text-gray-900">
                      {nameMap[item.deposit_register_id] || (
                        <span className="text-gray-400 italic font-normal text-xs">Betöltés...</span>
                      )}
                    </td>
                    <td className="p-3.5 text-gray-500 font-mono text-xs">
                      <span
                        className={`px-1.5 py-0.5 rounded ${
                          strategyLabel === 'BRAVE'
                            ? 'bg-indigo-50 text-indigo-600'
                            : 'bg-gray-100 text-gray-600'
                        }`}
                      >
                        {strategyLabel}
                      </span>
                    </td>

                    {/* Eszközök és az ÚJ USD alapú alszámlák 
                    <td className="p-3.5 font-mono text-xs text-gray-600 space-y-1">
                      <div className="flex items-center gap-4 border-b border-gray-100 pb-1 mb-1">
                        <div className="flex items-center gap-1">
                          <span className="text-gray-400 font-medium">BTC:</span>
                          <span className="font-medium text-gray-800">
                            {typeof item.btc_quantity === 'number' ? item.btc_quantity.toFixed(6) : '0.000000'}
                          </span>
                        </div>
                        <div className="flex items-center gap-1">
                          <span className="text-gray-400 font-medium">ETH:</span>
                          <span className="font-medium text-gray-800">
                            {typeof item.eth_quantity === 'number' ? item.eth_quantity.toFixed(6) : '0.000000'}
                          </span>
                        </div>
                      </div>
                      <div className="grid grid-cols-1 gap-0.5 text-xxs text-gray-500">
                        <div><span className="text-gray-400">Likviditás:</span> ${liqUsd.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</div>
                        <div><span className="text-gray-400">Bányászat:</span> ${mineUsd.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</div>
                        <div><span className="text-gray-400">Alt:</span> ${vaultUsd.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</div>
                      </div>
                    </td>*/}

                    {/* Befektetett */}
                    <td className="p-3.5 text-right font-mono tabular-nums">
                      <div className="text-gray-700 font-medium">
                        €{(item.invested_amount_eur || 0).toLocaleString()}
                      </div>
                      <div className="text-gray-400 text-xxs">
                        ${(item.invested_amount_usd || 0).toLocaleString()}
                      </div>
                    </td>

                    {/* Bónusz / Tőkerész */}
                    <td className="p-3.5 text-right font-mono tabular-nums whitespace-nowrap">
                      {hasBonusOrCapital ? (
                        <div className="space-y-0.5">
                          <div className="text-gray-700 font-medium">
                            <span className="text-gray-400 text-xxs font-sans mr-1">Bónusz:</span> €
                            {bonus.toLocaleString()}
                          </div>
                          <div className="text-gray-500 text-xxs">
                            <span className="text-gray-400 mr-1">Tőkerész:</span> €
                            {capitalRatio.toLocaleString()}
                          </div>
                        </div>
                      ) : (
                        <span className="text-amber-600 bg-amber-50 px-2 py-1 rounded-md text-xxs font-medium border border-amber-100">
                          Nincs
                        </span>
                      )}
                    </td>

                    {/* Szimulált érték */}
                    <td className="p-3.5 text-right font-mono tabular-nums">
                      <div className="text-gray-900 font-semibold text-sm">
                        €{Math.round(item.current_value_eur || 0).toLocaleString()}
                      </div>
                      <div className="text-gray-500 text-xxs font-medium">
                        ${Math.round(item.current_value_usd || 0).toLocaleString()}
                      </div>
                    </td>

                    {/* Safe PnL és Kifizetés */}
                    <td className="p-3.5 text-right font-mono tabular-nums">
                      <div
                        className={`font-bold text-sm ${
                          isProfit ? 'text-emerald-600' : 'text-rose-600'
                        }`}
                      >
                        {isProfit ? '+' : ''}
                        {typeof pnl === 'number' ? pnl.toFixed(2) : '0.00'}%
                      </div>
                      <div className="text-xxs mt-0.5">
                        {typeof item.total_payouts_eur === 'number' && item.total_payouts_eur > 0 ? (
                          <span className="text-slate-700 font-medium bg-slate-100 px-1.5 py-0.5 rounded">
                            Kivét: €
                            {item.total_payouts_eur.toLocaleString(undefined, {
                              minimumFractionDigits: 2,
                            })}
                          </span>
                        ) : (
                          <span className="text-gray-300 italic">Nincs kivét</span>
                        )}
                      </div>
                    </td>

                    {/* What-If gomb */}
                    <td className="p-3.5 text-center">
                      {item.what_if ? (
                        <button
                          onClick={() => setWhatIfModalId(item.deposit_register_id)}
                          className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-xxs font-medium text-gray-600 bg-gray-100 hover:bg-gray-900 hover:text-white border border-gray-200 transition-colors"
                        >
                          <svg
                            className="h-3 w-3"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
                            />
                          </svg>
                          Részletek
                        </button>
                      ) : (
                        <span className="text-gray-300 text-xxs">—</span>
                      )}
                    </td>
                  </tr>
                );
              })}

              {(!filteredAndSortedTopList || filteredAndSortedTopList.length === 0) && (
                <tr>
                  <td
                    colSpan={11}
                    className="text-center p-12 text-gray-400 italic text-sm bg-gray-50/20"
                  >
                    {/* --- MÓDOSÍTVA: fül-tudatos üres állapot --- */}
                    {loading
                      ? 'Várakozás az élő adatokra...'
                      : `Nincs megjeleníthető ${activeTab} deposit.`}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* What-If modal */}
      {whatIfModalId !== null && activeModalItem?.what_if && (
        <WhatIfModal
          report={activeModalItem.what_if}
          userName={nameMap[whatIfModalId] || ''}
          depositId={whatIfModalId}
          currentValueUsd={activeModalItem.current_value_usd}
          onClose={() => setWhatIfModalId(null)}
        />
      )}
    </div>
  );
};